import { type ReactNode, useState } from 'react';
import { getAppIcon } from '@/components/agents/app-icons';
import { EntityIcon } from '@/components/ui/entity-icon';
import { cn } from '@/lib/utils';

export interface ConnectorDisplayData {
  key: string;
  name: string;
  description?: string | null;
  icon?: string | null;
  /** Direct image URL (model-provider catalog icons); wins over faviconDomain. */
  iconUrl?: string | null;
  faviconDomain?: string | null;
}

interface ConnectorDefinitionLike {
  key: string;
  name: string;
  description?: string | null;
  icon?: string | null;
  favicon_domain?: string | null;
  auth_schema?: Record<string, unknown> | null;
}

function buildConnectorDefinitionMap<T extends ConnectorDefinitionLike>(definitions: T[]) {
  return new Map(definitions.map((definition) => [definition.key, definition]));
}

const RUNTIME_PLATFORM_LABELS: Record<string, string> = {
  ios: 'iOS',
  android: 'Android',
  macos: 'macOS',
  windows: 'Windows',
  linux: 'Linux',
};

/**
 * Format a connector's `runtime.platforms` list into a human label like
 * "macOS" or "macOS, iOS". Returns null for cloud-only connectors (no
 * runtime restriction) so callers can skip rendering a "Requires …" badge.
 */
export function formatRuntimePlatforms(
  platforms: string[] | null | undefined
): string | null {
  if (!platforms?.length) return null;
  return platforms.map((p) => RUNTIME_PLATFORM_LABELS[p] ?? p).join(', ');
}

const AUTH_METHOD_LABELS: Record<string, string> = {
  oauth: 'OAuth',
  browser: 'Browser',
  apiKey: 'API key',
  api_key: 'API key',
  env: 'API key',
  env_keys: 'API key',
  interactive: 'Interactive',
};

/**
 * Returns the unique, human-readable auth methods a connector supports
 * (e.g. ["OAuth", "Browser"] for a connector that offers both). `"none"`
 * is filtered out because a connector that requires no auth has nothing
 * to surface. Order follows first-occurrence in `auth_schema.methods`.
 */
export function getAuthMethodLabels(
  authSchema: Record<string, unknown> | null | undefined
): string[] {
  if (!authSchema) return [];
  const methods = (authSchema as { methods?: Array<Record<string, unknown>> }).methods;
  if (!Array.isArray(methods)) return [];
  // Dedupe by *rendered* label so aliases like `env`/`env_keys`/`apiKey` that
  // map to the same human string ("API key") collapse to one badge.
  const seenLabels = new Set<string>();
  const labels: string[] = [];
  for (const method of methods) {
    const type = method.type;
    if (typeof type !== 'string' || type === 'none') continue;
    const label = AUTH_METHOD_LABELS[type] ?? type;
    if (seenLabels.has(label)) continue;
    seenLabels.add(label);
    labels.push(label);
  }
  return labels;
}

/**
 * Extract the first OAuth provider domain from a connector's auth_schema.
 * Uses the authorizationUrl or provider name to derive a domain for favicon lookup.
 */
export function extractOAuthDomain(
  authSchema: Record<string, unknown> | null | undefined
): string | null {
  if (!authSchema) return null;
  const methods = (authSchema as { methods?: Array<Record<string, unknown>> }).methods;
  if (!Array.isArray(methods)) return null;

  for (const method of methods) {
    if (method.type !== 'oauth') continue;

    // If the method has an explicit authorization URL, extract domain from it
    const authUrl = method.authorization_url ?? method.authorizationUrl;
    if (typeof authUrl === 'string') {
      try {
        return new URL(authUrl).hostname;
      } catch {
        // not a valid URL
      }
    }

    // Fall back to provider name as domain (e.g. "reddit" → "reddit.com")
    if (typeof method.provider === 'string' && method.provider.length > 0) {
      return `${method.provider}.com`;
    }
  }

  return null;
}

export function resolveConnectorDisplay(
  connectorKey: string,
  definitions: Map<string, ConnectorDefinitionLike> | ConnectorDefinitionLike[],
  fallback?: Partial<ConnectorDisplayData>
): ConnectorDisplayData {
  const definitionMap = Array.isArray(definitions)
    ? buildConnectorDefinitionMap(definitions)
    : definitions;
  const definition = definitionMap.get(connectorKey);

  return {
    key: connectorKey,
    name: definition?.name || fallback?.name || connectorKey,
    description: definition?.description ?? fallback?.description ?? null,
    icon: definition?.icon ?? fallback?.icon ?? null,
    // Definitions never carry a direct image URL — only synthesized rows
    // (model providers joined to the inference catalog) supply one.
    iconUrl: fallback?.iconUrl ?? null,
    faviconDomain:
      definition?.favicon_domain ??
      extractOAuthDomain(definition?.auth_schema) ??
      fallback?.faviconDomain ??
      null,
  };
}

function getConnectorMonogram(name: string): string {
  const compact = name.trim();
  if (!compact) return '?';

  // Strip non-alphanumeric characters, then split into words
  const cleaned = compact.replace(/[^a-zA-Z0-9\s.-]/g, '').trim();
  const words = cleaned.split(/[\s.-]+/).filter(Boolean);
  if (words.length >= 2) {
    return `${words[0][0] ?? ''}${words[1][0] ?? ''}`.toUpperCase();
  }

  return (cleaned || compact).slice(0, 2).toUpperCase();
}

export function ConnectorMark({
  icon,
  iconUrl,
  name,
  faviconDomain,
  plain = false,
  fallback,
  className,
}: {
  icon?: string | null;
  /** Direct image URL (model-provider catalog icons); wins over faviconDomain. */
  iconUrl?: string | null;
  name: string;
  faviconDomain?: string | null;
  /** Renders without the bordered tile — for compact rails and lists. */
  plain?: boolean;
  fallback?: ReactNode;
  className?: string;
}) {
  const [faviconError, setFaviconError] = useState(false);
  const [iconUrlError, setIconUrlError] = useState(false);
  const tileClassName = plain
    ? 'text-muted-foreground'
    : 'rounded-lg border bg-muted/40 text-muted-foreground';

  if (icon) {
    return (
      <span
        className={cn(
          'flex h-8 w-8 shrink-0 items-center justify-center',
          tileClassName,
          className
        )}
      >
        <EntityIcon icon={icon} className="h-4 w-4" fallback={getConnectorMonogram(name)} />
      </span>
    );
  }

  if (iconUrl && !iconUrlError) {
    return (
      <span
        className={cn(
          'flex h-8 w-8 shrink-0 items-center justify-center',
          tileClassName,
          className
        )}
      >
        <img
          src={iconUrl}
          alt={name}
          width={16}
          height={16}
          className="h-4 w-4"
          onError={() => setIconUrlError(true)}
        />
      </span>
    );
  }

  const brandIcon = getAppIcon(name);
  if (brandIcon) {
    return (
      <span
        className={cn(
          'flex h-8 w-8 shrink-0 items-center justify-center',
          tileClassName,
          className
        )}
      >
        {brandIcon}
      </span>
    );
  }

  if (faviconDomain && !faviconError) {
    return (
      <span
        className={cn(
          'flex h-8 w-8 shrink-0 items-center justify-center',
          tileClassName,
          className
        )}
      >
        <img
          src={`https://www.google.com/s2/favicons?domain=${encodeURIComponent(faviconDomain)}&sz=32`}
          alt={name}
          width={16}
          height={16}
          className="h-4 w-4"
          onError={() => setFaviconError(true)}
        />
      </span>
    );
  }

  return (
    <span
      className={cn(
        'flex h-8 w-8 shrink-0 items-center justify-center text-[11px] font-semibold uppercase tracking-wide',
        tileClassName,
        className
      )}
    >
      {fallback ?? getConnectorMonogram(name)}
    </span>
  );
}

export function ConnectorDisplay({
  connector,
  className,
  nameClassName,
  descriptionClassName,
  showDescription = true,
}: {
  // Accepts both the normalized `ConnectorDisplayData` shape (`faviconDomain`)
  // and the raw API shape (`favicon_domain`). The API objects only carry the
  // snake_case field, so failing to read it here drops the favicon for every
  // non-OAuth connector.
  connector: ConnectorDisplayData & {
    favicon_domain?: string | null;
    auth_schema?: Record<string, unknown> | null;
  };
  className?: string;
  nameClassName?: string;
  descriptionClassName?: string;
  showDescription?: boolean;
}) {
  const faviconDomain =
    connector.faviconDomain ?? connector.favicon_domain ?? extractOAuthDomain(connector.auth_schema);
  return (
    <div className={cn('flex min-w-0 items-center gap-3', className)}>
      <ConnectorMark
        icon={connector.icon}
        iconUrl={connector.iconUrl}
        name={connector.name}
        faviconDomain={faviconDomain}
      />
      <div className="min-w-0">
        <div className={cn('truncate text-sm font-medium', nameClassName)}>{connector.name}</div>
        {showDescription && connector.description && (
          <p className={cn('truncate text-xs text-muted-foreground', descriptionClassName)}>
            {connector.description}
          </p>
        )}
      </div>
    </div>
  );
}
